import { withRequestDeadline } from "./requestDeadline";
import { postFormWithProgress } from "./uploadWithProgress";
import type { UploadProgress } from "./uploadWithProgress";
import { pause, whenVisibleAndOnline } from "./whenReachable";
import { cleanBrandKitWebsite } from "./brandKitWebsite";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { deleteObject, ref as storageRef, uploadBytes } from "firebase/storage";
import { BrandAsset, BrandKit } from "../models/BrandKit";
import { db, getSignedUrl, storage } from "../services/Firebase";
import {
  BrandKitAssetUploadError,
  BrandKitSaveError,
  normalizeBrandKitAssetUploadErrorCode,
  normalizeBrandKitSaveErrorCode,
} from "./brandKitSaveErrors";

/**
 * Extracts brand kit data from a website URL using the backend extraction service.
 */
export type BrandKitExtraction = Awaited<ReturnType<typeof extractBrandKitFromUrl>>;

export const extractBrandKitFromUrl = async (
  url: string,
  idToken: string,
  brandKitId: string,
  workspaceId?: string,
  signal?: AbortSignal,
  onProgress?: (stage: string) => void,
  /** Keep this scan's outcome server-side so a dropped connection can pick it up. */
  options: { keepResult?: boolean; scanId?: string } = {}
): Promise<{
  brandName: string;
  tagline: string;
  businessDescription: string;
  targetAudiences: string[];
  website: string;
  colors: {
    primaryColor: string | null;
    secondaryColor: string | null;
    tertiaryColor: string | null;
    additionalColors?: string[];
  };
  fonts: {
    primaryFont: string | null;
    secondaryFont: string | null;
    bodyFont: string | null;
  };
  voiceTone: "formal" | "casual" | "friendly" | "professional" | null;
  voiceInstructions: string;
  primarySocial: { platform: string; handle: string } | null;
  logoPath: string | null;
  brandAssets: BrandAsset[];
  assetWarning?: boolean;
  tempId: string;
}> => {
  return withRequestDeadline(
    async (requestSignal) => {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/brandkit/extract-from-url`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            ...(workspaceId ? { "x-workspace-id": workspaceId } : {}),
            "Content-Type": "application/json",
          },
          signal: requestSignal,
          body: JSON.stringify({
            url,
            brandKitId,
            stream: !!onProgress,
            ...(options.keepResult && options.scanId
              ? { keepResult: true, scanId: options.scanId }
              : {}),
          }),
        }
      );

      if (!response.ok) {
        const body = await response.json();
        const err = new Error(
          body.message || body.error || "Failed to extract brand kit from URL"
        );
        (err as any).code = body.code;
        throw err;
      }

      if (
        !response.headers?.get("content-type")?.includes("application/x-ndjson")
      )
        return response.json();
      const reader = response.body?.getReader();
      if (!reader)
        throw Object.assign(
          new Error("The website scan was interrupted. Please try again."),
          { code: "SCAN_INTERRUPTED" }
        );
      const decoder = new TextDecoder();
      let pending = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          pending += decoder.decode(value, { stream: !done });
          const lines = pending.split("\n");
          pending = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim()) continue;
            const event = JSON.parse(line);
            if (event.type === "progress") onProgress?.(event.stage);
            if (event.type === "error")
              throw Object.assign(new Error(event.message), {
                code: event.code,
              });
            if (event.type === "result") return event.data;
          }
          if (done)
            throw Object.assign(
              new Error("The website scan was interrupted. Please try again."),
              { code: "SCAN_INTERRUPTED" }
            );
        }
      } finally {
        reader.releaseLock();
      }
    },
    120000,
    "Your website is taking too long to respond. Try again or set up your brand manually.",
    signal
  );
};

type KeptBrandKitScan =
  | { status: "none" | "running" }
  | { status: "done"; result: BrandKitExtraction }
  | { status: "failed"; code: string; message: string };

/**
 * Picks up a kept website scan after the connection dropped mid-scan. Waits
 * until the tab is visible and online, then polls while the scan is still
 * running. Returns null when there is nothing to pick up (scan again); throws
 * the scan's own error when it failed, or a timeout when it's still running at
 * the deadline (a second scan would only race the first).
 */
export const recoverBrandKitScan = async ({
  url,
  brandKitId,
  scanId,
  getIdToken,
  workspaceId,
  signal,
  timeoutMs = 90000,
  pollMs = 2500,
}: {
  url: string;
  brandKitId: string;
  scanId: string;
  getIdToken: () => Promise<string>;
  workspaceId?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  pollMs?: number;
}): Promise<BrandKitExtraction | null> => {
  await whenVisibleAndOnline(signal);
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    let scan: KeptBrandKitScan | null = null;
    try {
      scan = await withRequestDeadline(
        async (requestSignal) => {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE}/brandkit/scan-result/${encodeURIComponent(
              brandKitId
            )}?url=${encodeURIComponent(url)}&scanId=${encodeURIComponent(scanId)}`,
            {
              signal: requestSignal,
              headers: {
                Authorization: `Bearer ${await getIdToken()}`,
                ...(workspaceId ? { "x-workspace-id": workspaceId } : {}),
              },
            }
          );
          // An error response is an answer, not a network hiccup: stop polling.
          if (!response.ok) return { status: "none" };
          return response.json();
        },
        15000,
        "Loading the scan result took too long.",
        signal
      );
    } catch (error) {
      if (signal?.aborted) throw error;
      // The network may still be waking up; keep trying until the deadline.
    }
    if (scan?.status === "done") return scan.result;
    if (scan?.status === "failed")
      throw Object.assign(new Error(scan.message), { code: scan.code });
    if (scan?.status === "none") return null;
    if (Date.now() >= deadline)
      throw Object.assign(
        new Error(
          "Your website is taking too long to respond. Try again or set up your brand manually."
        ),
        { name: "TimeoutError" }
      );
    await pause(pollMs, signal);
  }
};

/**
 * Asks the server to (re)derive the kit's color pairings (cached on the kit
 * doc) and returns them. Pass the editor's current colors so "Pair now"
 * reflects unsaved edits.
 */
export const refreshBrandKitColorSystem = async (
  brandKitId: string,
  idToken: string,
  current?: {
    colors?: BrandKit["colors"];
  }
): Promise<BrandKit["colorSystem"] | null> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE}/brandkit/refresh-color-system`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        brandKitId,
        colors: current?.colors,
      }),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to derive color pairings");
  }
  const data = await response.json();
  return data.colorSystem ?? null;
};

/**
 * Uploads a visual asset (photo or logo) for a brand kit to Firebase Storage.
 * Uses backend endpoint for validation and SVG-to-PNG conversion.
 */
export const uploadBrandKitVisualAsset = async (
  userId: string,
  brandKitId: string,
  file: File,
  idToken: string,
  options?: { skipPreview?: boolean; onProgress?: (progress: UploadProgress) => void }
): Promise<{ path: string; url: string; converted?: boolean }> => {
  // Use backend endpoint for upload with SVG conversion
  const formData = new FormData();
  formData.append("file", file);
  formData.append("brandKitId", brandKitId);

  const response = await postFormWithProgress(
    `${process.env.REACT_APP_API_BASE}/brandkit/upload-logo`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "x-workspace-id": userId,
      },
      onProgress: options?.onProgress,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to upload logo");
  }

  const result = await response.json();

  // Get download URL from storage
  const assetStorageRefInstance = storageRef(storage, result.path);
  // Saving the upload must not depend on the preview request succeeding.
  // The preview component loads this path independently with a timeout.
  const downloadUrl = options?.skipPreview
    ? ""
    : await getSignedUrl(assetStorageRefInstance.fullPath);

  return {
    path: result.path,
    url: downloadUrl,
    converted: result.converted,
  };
};

/**
 * Uploads B-Roll videos for a brand kit to Firebase Storage.
 * @deprecated Use uploadBrandKitAsset instead for images
 */
export const uploadBrandKitBRollVideos = async (
  userId: string,
  brandKitId: string,
  files: File[]
): Promise<string[]> => {
  const uploadPromises = files.map(async (file, index) => {
    const fileExtension = file.name.split(".").pop() || "mp4";
    const videoFileName = `broll_${Date.now()}_${index}.${fileExtension}`;
    const videoStoragePath = `brandKits/${userId}/${brandKitId}/broll/${videoFileName}`;
    const videoStorageRefInstance = storageRef(storage, videoStoragePath);

    await uploadBytes(videoStorageRefInstance, file);
    return videoStoragePath;
  });

  const results = await Promise.all(uploadPromises);
  return results;
};

/**
 * Uploads a brand asset (image) for use in content generation.
 * The asset is uploaded via the backend which handles validation,
 * thumbnail generation, and optionally enqueues a task for description generation.
 *
 * @param draftMode - If true, uploads to storage only without adding to Firestore
 *                    or enqueuing processing. Draft assets are committed on save.
 */
export const uploadBrandKitAsset = async (
  brandKitId: string,
  file: File,
  idToken: string,
  draftMode = false,
  workspaceId?: string,
  options?: { onProgress?: (progress: UploadProgress) => void }
): Promise<{ asset: BrandAsset; message: string; isDraft?: boolean }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("brandKitId", brandKitId);
  if (draftMode) {
    formData.append("draftMode", "true");
  }

  const response = await postFormWithProgress(
    `${process.env.REACT_APP_API_BASE}/brandkit/upload-asset`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(workspaceId ? { "x-workspace-id": workspaceId } : {}),
      },
      onProgress: options?.onProgress,
    }
  );

  if (!response.ok) {
    let errorData: { code?: unknown; error?: string; message?: string } = {};
    try {
      errorData = await response.json();
    } catch {
      // Keep non-JSON proxy errors in logs without exposing them to users.
    }

    throw new BrandKitAssetUploadError(
      normalizeBrandKitAssetUploadErrorCode(response.status, errorData.code),
      response.status,
      errorData.error ||
        errorData.message ||
        `Brand kit asset upload failed with status ${response.status}`
    );
  }

  return response.json();
};

/**
 * Atomically applies draft additions and pending deletions in Firestore, then
 * enqueues processing tasks for newly added assets.
 */
export const commitBrandKitAssets = async (
  brandKitId: string,
  assets: BrandAsset[],
  idToken: string,
  assetIdsToDelete: string[] = [],
  workspaceId?: string
): Promise<{ message: string; count: number; deletedCount: number }> => {
  return withRequestDeadline(async (signal) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/brandkit/commit-assets`,
      {
        method: "POST",
        signal,
        headers: {
          Authorization: `Bearer ${idToken}`,
          ...(workspaceId ? { "x-workspace-id": workspaceId } : {}),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ brandKitId, assets, assetIdsToDelete }),
      }
    );

    if (!response.ok) {
      let errorData: { code?: unknown; error?: string; message?: string } = {};
      try {
        errorData = await response.json();
      } catch {
        // Some proxies and network layers return an empty or non-JSON response.
      }

      throw new BrandKitSaveError(
        normalizeBrandKitSaveErrorCode(response.status, errorData.code),
        response.status,
        errorData.error ||
          errorData.message ||
          `Brand kit asset commit failed with status ${response.status}`
      );
    }

    return response.json();
  });
};

/**
 * Deletes draft assets from storage.
 * Called when user cancels/closes without saving.
 */
export const deleteDraftBrandKitAssets = async (
  brandKitId: string,
  assetPaths: { storagePath: string; thumbnailPath?: string }[],
  idToken: string
): Promise<{ message: string }> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE}/brandkit/delete-draft-assets`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ brandKitId, assetPaths }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to delete draft assets");
  }

  return response.json();
};

/**
 * Creates a new brand kit document in Firestore.
 */
export const createBrandKit = async (
  userId: string,
  kitData: Omit<BrandKit, "id" | "userId" | "createdAt" | "updatedAt">,
  visualAssetFile?: File,
  bRollFiles?: File[],
  idToken?: string
): Promise<string> => {
  const newKitDocRef = doc(collection(db, "brand_kits"));
  const firestoreDocId = newKitDocRef.id;

  let visualAssetPath: string | undefined = undefined;
  let bRollVideos: string[] = [];

  if (visualAssetFile) {
    if (!idToken) {
      throw new Error("Authentication token required for logo upload");
    }
    const uploadResult = await uploadBrandKitVisualAsset(
      userId,
      firestoreDocId,
      visualAssetFile,
      idToken
    );
    visualAssetPath = uploadResult.path;

    if (uploadResult.converted) {
      console.log("Logo was automatically converted from SVG to PNG");
    }
  }

  if (bRollFiles && bRollFiles.length > 0) {
    bRollVideos = await uploadBrandKitBRollVideos(
      userId,
      firestoreDocId,
      bRollFiles
    );
  }

  const newKitDataForDoc: Omit<BrandKit, "id"> = {
    userId,
    workspaceId: userId,
    name: kitData.name,
    logoPath: visualAssetPath || kitData.logoPath,
    brandName: kitData.brandName,
    tagline: kitData.tagline,
    businessDescription: kitData.businessDescription,
    targetAudiences: kitData.targetAudiences,
    website:
      kitData.website === undefined
        ? undefined
        : cleanBrandKitWebsite(kitData.website),
    primarySocial: kitData.primarySocial,
    primaryBrandElement: kitData.primaryBrandElement,
    fonts: kitData.fonts,
    colors: kitData.colors,
    outroSettings: kitData.outroSettings,
    watermarkSettings: kitData.watermarkSettings,
    bRollVideos: bRollVideos.length > 0 ? bRollVideos : undefined,
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
  };

  await setDoc(newKitDocRef, newKitDataForDoc);
  return firestoreDocId;
};

/**
 * Updates an existing brand kit in Firestore.
 */
export const updateBrandKit = async (
  kitId: string,
  kitData: Partial<Omit<BrandKit, "id" | "userId" | "createdAt" | "updatedAt">>,
  visualAssetFile?: File,
  userId?: string,
  bRollFiles?: File[],
  existingBRollPaths?: string[],
  idToken?: string
): Promise<void> => {
  const kitDocRef = doc(db, "brand_kits", kitId);
  const updatePayload: Partial<
    Omit<BrandKit, "id" | "userId" | "createdAt">
  > & {
    updatedAt: Timestamp;
    bRollVideos?: string[];
  } = {
    ...(kitData as Partial<
      Omit<BrandKit, "id" | "userId" | "createdAt" | "updatedAt">
    >),
    updatedAt: serverTimestamp() as Timestamp,
  };

  if (typeof updatePayload.website === "string") {
    updatePayload.website = cleanBrandKitWebsite(updatePayload.website);
  }

  // Handle visual asset updates
  if (visualAssetFile && userId) {
    if (!idToken) {
      throw new Error("Authentication token required for logo upload");
    }
    const existingKitSnap = await getDoc(kitDocRef);
    if (existingKitSnap.exists()) {
      const existingKitData = existingKitSnap.data() as BrandKit;
      if (existingKitData.logoPath) {
        try {
          const oldAssetRef = storageRef(storage, existingKitData.logoPath);
          await deleteObject(oldAssetRef);
        } catch (error) {
          console.warn(
            "Could not delete old visual asset during update:",
            error
          );
        }
      }
    }
    const uploadResult = await uploadBrandKitVisualAsset(
      userId,
      kitId,
      visualAssetFile,
      idToken
    );
    updatePayload.logoPath = uploadResult.path;

    if (uploadResult.converted) {
      console.log("Logo was automatically converted from SVG to PNG");
    }
  }

  // Handle B-Roll video updates
  if (
    (bRollFiles && bRollFiles.length > 0) ||
    existingBRollPaths !== undefined
  ) {
    // Get existing data to manage B-Roll videos
    const existingKitSnap = await getDoc(kitDocRef);
    let currentBRollVideos: string[] = [];

    if (existingKitSnap.exists()) {
      const existingKitData = existingKitSnap.data() as BrandKit;
      currentBRollVideos = existingKitData.bRollVideos || [];
    }

    // Start with existing videos that should be kept
    const keptExistingVideos = existingBRollPaths || [];

    // Find videos that were removed and delete them from storage
    const pathsToDelete = currentBRollVideos.filter(
      (path) => !keptExistingVideos.includes(path)
    );

    if (pathsToDelete.length > 0) {
      const deletePromises = pathsToDelete.map(async (videoPath) => {
        try {
          const oldVideoRef = storageRef(storage, videoPath);
          await deleteObject(oldVideoRef);
        } catch (error) {
          console.warn(
            `Could not delete removed B-Roll video ${videoPath}:`,
            error
          );
        }
      });
      await Promise.all(deletePromises);
    }

    // Upload new B-Roll videos if any
    let newVideoPaths: string[] = [];
    if (bRollFiles && bRollFiles.length > 0 && userId) {
      newVideoPaths = await uploadBrandKitBRollVideos(
        userId,
        kitId,
        bRollFiles
      );
    }

    // Combine existing videos (that weren't removed) with new videos
    updatePayload.bRollVideos = [...keptExistingVideos, ...newVideoPaths];
  }

  await updateDoc(kitDocRef, updatePayload as any);
};

/**
 * Fetches all brand kits for a given user from Firestore.
 */
export const getBrandKitsForUser = async (
  userId: string
): Promise<BrandKit[]> => {
  const brandKitsRef = collection(db, "brand_kits");
  const q = query(brandKitsRef, where("userId", "==", userId));

  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as BrandKit)
    );
  } catch (error) {
    console.error("Error fetching brand kits:", error);
    return [];
  }
};

/**
 * Deletes a brand kit from Firestore and its associated visual asset and B-Roll videos from Firebase Storage.
 */
export const deleteBrandKit = async (
  kitId: string,
  visualAssetPath?: string,
  bRollVideos?: string[]
): Promise<void> => {
  const kitDocRef = doc(db, "brand_kits", kitId);

  // Delete the Firestore document
  await deleteDoc(kitDocRef);

  // If a visualAssetPath is provided, attempt to delete the visual asset from Firebase Storage
  if (visualAssetPath) {
    try {
      const assetStorageRefInstance = storageRef(storage, visualAssetPath);
      await deleteObject(assetStorageRefInstance);
      console.log(`Successfully deleted visual asset: ${visualAssetPath}`);
    } catch (error) {
      // Log an error if the visual asset deletion fails, but don't let it block the overall success
      // as the Firestore document deletion is the primary concern.
      // Common reasons for failure: file doesn't exist, or permissions issues.
      console.warn(
        `Failed to delete visual asset from storage (${visualAssetPath}):`,
        error
      );
    }
  }

  // If B-Roll videos are provided, attempt to delete them from Firebase Storage
  if (bRollVideos && bRollVideos.length > 0) {
    const deletePromises = bRollVideos.map(async (videoPath) => {
      try {
        const videoStorageRefInstance = storageRef(storage, videoPath);
        await deleteObject(videoStorageRefInstance);
        console.log(`Successfully deleted B-Roll video: ${videoPath}`);
      } catch (error) {
        console.warn(
          `Failed to delete B-Roll video from storage (${videoPath}):`,
          error
        );
      }
    });
    await Promise.all(deletePromises);
  }
};
